import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import LoadingOrError from '../../Loader/LoadingOrError';

/**
 * Helper function to extract the GraphQL variable type from a query definition
 * @param {Object} query - GraphQL query document
 * @param {string} variableName - Name of the variable to check
 * @returns {string|null} - The unwrapped type kind ('ListType', 'NamedType', etc.) or null if not found/malformed
 */
function getGraphQLVariableType(query, variableName) {
  if (!query?.definitions) {
    console.warn('ShowTable: Query has no definitions', query);
    return null;
  }

  // Find the OperationDefinition
  const operationDefinition = query.definitions.find(
    (definition) => definition.kind === 'OperationDefinition',
  );

  if (!operationDefinition) {
    console.warn('ShowTable: Query has no OperationDefinition', query);
    return null;
  }

  if (!operationDefinition.variableDefinitions) {
    console.warn(
      'ShowTable: OperationDefinition has no variableDefinitions',
      operationDefinition,
    );
    return null;
  }

  // Find the variable definition for the specified variable name
  const variableDefinition = operationDefinition.variableDefinitions.find(
    (definition) => definition.variable?.name?.value === variableName,
  );

  if (!variableDefinition) {
    // Variable not found in query - this is expected for queries that don't use this variable
    return null;
  }

  // Unwrap NonNullType wrappers to get to the underlying type
  let typeNode = variableDefinition.type;
  while (typeNode?.kind === 'NonNullType') {
    typeNode = typeNode.type;
  }

  return typeNode?.kind || null;
}

function ShowTable({
  partnerOrganizationId = null,
  partnerLocationId = null,
  dataTable: DataTable,
  query,
  loadingTitle,
  newTo,
  hideOrg = false,
  hideLoc = false,
  dataKey,
}) {
  let variables = {};
  const linkParamsParts = [];

  if (partnerOrganizationId) {
    variables = { partnerOrganizationId };
    linkParamsParts.push(`partnerOrganizationId=${partnerOrganizationId}`);
  }

  if (partnerLocationId) {
    // Parse GraphQL query definition to check if partnerLocationId is a list type
    // This is more robust than hardcoding query names
    const variableType = getGraphQLVariableType(query, 'partnerLocationId');
    const isListType = variableType === 'ListType';

    // If variable type is not found or parsing failed, default to scalar (single ID)
    // This is safe because most queries expect single ID, and GET_USERS will have ListType
    variables = {
      ...variables,
      partnerLocationId: isListType ? [partnerLocationId] : partnerLocationId,
    };
    linkParamsParts.push(`partnerLocationId=${partnerLocationId}`);
  }

  const linkParams =
    linkParamsParts.length > 0 ? `?${linkParamsParts.join('&')}` : '';

  const { error, loading, data } = useQuery(query, {
    variables,
  });

  const shouldRender = LoadingOrError({
    loading,
    error,
    data,
    dataKey,
    title: loadingTitle,
  });

  if (shouldRender === true) {
    return (
      <div className="flex flex-col gap-x-8">
        <DataTable
          data={data}
          height={500}
          newTo={`${newTo}${linkParams}`}
          hideLoc={hideLoc}
          hideOrg={hideOrg}
        />
      </div>
    );
  }

  return shouldRender;
}

ShowTable.propTypes = {
  partnerOrganizationId: PropTypes.string,
  partnerLocationId: PropTypes.string,
  dataTable: PropTypes.elementType.isRequired,
  query: PropTypes.object.isRequired,
  loadingTitle: PropTypes.string.isRequired,
  newTo: PropTypes.string.isRequired,
  hideLoc: PropTypes.bool,
  hideOrg: PropTypes.bool,
  dataKey: PropTypes.string,
};

export default ShowTable;
